import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';

import type {
	useUserByLicensedProductsQuery$data,
	useUserByLicensedProductsQuery$variables,
} from './__generated__/useUserByLicensedProductsQuery.graphql.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

const useUserByLicensedProductsQuery = graphql`
	query useUserByLicensedProductsQuery {
		confluence_tenantContext {
			licensedProducts {
				licenseStatus
				productKey
			}
		}
	}
`;

/**
 * Determine user type (land/xflow) by licensedProducts
 * This should only be used within the Onboarding experience and Account Setup Modal flow for admins
 *
 * @param {boolean} skip - if the GetLicensedProductsQuery should be skipped
 * @returns {boolean} isLandUser - determines if the user is a land user by licensedProducts
 * @returns {boolean} isXflowUser - determines if the user is a cross flow user by licensedProducts
 * @returns {boolean} loading - if GetLicensedProductsQuery is still loading
 * @returns {object} error - An error object representing any errors encountered during the query execution.
 */
export const useUserByLicensedProducts = ({
	skip = false,
}: {
	skip?: boolean;
}): {
	isLandUser: boolean;
	isXflowUser: boolean;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
} => {
	const isLicensed = useIsLicensed();

	const skipConditions = skip || !isLicensed;

	const {
		data: licensedProductsData,
		loading: licensedProductsLoading,
		error: licensedProductsError,
	} = useQueryInterop<
		useUserByLicensedProductsQuery$data,
		useUserByLicensedProductsQuery$variables
	>(useUserByLicensedProductsQuery, {
		errorPolicy: 'all',
		skip: skipConditions,
	});

	if (licensedProductsLoading || licensedProductsError || skipConditions) {
		return {
			isLandUser: false,
			isXflowUser: false,
			loading: licensedProductsLoading,
			error: licensedProductsError,
		};
	}

	const licensedProducts = licensedProductsData?.confluence_tenantContext?.licensedProducts;
	const userByLicensedProducts = checkUserByLicensedProducts(licensedProducts);

	return {
		isLandUser: skipConditions ? false : userByLicensedProducts.isLandUser,
		isXflowUser: skipConditions ? false : userByLicensedProducts.isXflowUser,
		loading: licensedProductsLoading,
		error: licensedProductsError,
	};
};

/**
 *
 * @param licensedProducts
 * @returns
 * isXflowUser - if user has any active jira product
 * isLandUser - if user has only 1 active product and it's confluence
 *
 */
function checkUserByLicensedProducts(
	licensedProducts:
		| ReadonlyArray<{
				readonly licenseStatus: string;
				readonly productKey: string;
		  }>
		| null
		| undefined,
) {
	if (!licensedProducts) {
		return { isXflowUser: false, isLandUser: false };
	}

	const isXflowUser = licensedProducts.some((product) =>
		product.productKey.toLowerCase().includes('jira'),
	);
	return {
		isXflowUser,
		isLandUser: !isXflowUser,
	};
}
